<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import WhatsappStickersAPI from 'dashboard/api/whatsappStickers';
import { useMessageContext } from '../provider.js';

const { attachments } = useMessageContext();
const { t } = useI18n();

const attachment = computed(() => {
  return attachments.value[0];
});

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();
const isSaved = ref(false);
const isSaving = ref(false);

onMounted(() => {
  if (attachment.value?.dataUrl) {
    loadWithRetry(attachment.value.dataUrl);
  }
});

const handleImageError = () => {
  hasError.value = true;
};

const saveSticker = async () => {
  try {
    isSaving.value = true;
    const response = await WhatsappStickersAPI.saveFromAttachment({
      attachmentId: attachment.value.id,
      favorite: true,
    });
    isSaved.value = true;
    useAlert(t('CONVERSATION.STICKERS.SAVE_SUCCESS'));
    return response;
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CONVERSATION.STICKERS.SAVE_ERROR')
    );
    return null;
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden !bg-transparent !border-0 !shadow-none !dark:bg-transparent !hover:bg-transparent"
    data-bubble-name="sticker"
  >
    <div
      v-if="hasError"
      class="flex items-center gap-1 text-center rounded-lg p-2 bg-n-alpha-1"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11 text-xs">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div
      v-else-if="isLoaded"
      class="relative group overflow-hidden max-w-[160px] max-h-[160px] select-none"
    >
      <img
        class="skip-context-menu object-contain w-full h-full pointer-events-none"
        :src="attachment.dataUrl"
        :width="attachment.width || 160"
        :height="attachment.height || 160"
        @error="handleImageError"
      />
      <div class="absolute right-1 bottom-1 hidden group-hover:flex">
        <Button
          xs
          solid
          slate
          :icon="isSaved ? 'i-lucide-check' : 'i-lucide-star'"
          class="opacity-80"
          :disabled="isSaving || isSaved"
          :title="
            isSaved
              ? $t('CONVERSATION.STICKERS.SAVED')
              : $t('CONVERSATION.STICKERS.SAVE')
          "
          @click.stop="saveSticker"
        />
      </div>
    </div>
  </BaseBubble>
</template>

<style scoped>
/* Ensure sticker bubble doesn't get default hover backgrounds or pointer events on the image itself */
img {
  -webkit-user-drag: none;
}
</style>
